import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/api';
import type { NotificationEmbedConfig, NotificationTemplate } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const DEFAULT_EMBED_COLOR = 0xff95ce;
const EMBED_TEMPLATE_KEYS = new Set([
  'pass.purchase.success',
  'pass.approved',
  'pass.manual.granted',
  'pass.extended',
]);

type TemplatePatch = {
  title: string | null;
  body: string;
  isEnabled: boolean;
  embedConfig?: NotificationEmbedConfig;
};

type TemplateUpdate = {
  id: number;
  patch: TemplatePatch;
};

const KEY_LABELS: Record<string, string> = {
  'auth.prompt.title': 'Логин: заголовок запроса',
  'auth.prompt.body': 'Логин: текст запроса с кнопками',
  'auth.approved': 'Логин: успешное подтверждение',
  'auth.denied': 'Логин: отказ',
  'pass.purchase.success': 'Проходка: успешная покупка',
  'pass.approved': 'Проходка: одобрение заявки',
  'pass.manual.granted': 'Проходка: ручная выдача',
  'pass.extended': 'Проходка: успешное продление',
  'pass.expiring.1week': 'Проходка: за неделю до окончания',
  'pass.expiring.1day': 'Проходка: за сутки до окончания',
  'pass.expiring.1hour': 'Проходка: за час до окончания',
  'pass.expired': 'Проходка: уведомление об окончании',
};

function colorToHex(value: number | null | undefined): string {
  return `#${(value ?? DEFAULT_EMBED_COLOR).toString(16).padStart(6, '0').slice(-6)}`;
}

function hexToColor(value: string): number {
  return /^#[0-9a-f]{6}$/i.test(value) ? Number.parseInt(value.slice(1), 16) : DEFAULT_EMBED_COLOR;
}

function nullable(value: string): string | null {
  return value.trim() || null;
}

function safeImageUrl(value: string | null | undefined): string | null {
  return value && /^https:\/\//i.test(value) ? value : null;
}

function isValidHttpsUrl(value: string | null | undefined): boolean {
  if (!value?.trim()) return true;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

export function AdminTemplates() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admin', 'templates'],
    queryFn: adminApi.templates.list,
  });
  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const [titleInput, setTitleInput] = useState('');
  const [bodyInput, setBodyInput] = useState('');
  const [enabledInput, setEnabledInput] = useState(true);
  const [embedInput, setEmbedInput] = useState<NotificationEmbedConfig>({
    color: DEFAULT_EMBED_COLOR,
  });
  const [colorInput, setColorInput] = useState(colorToHex(DEFAULT_EMBED_COLOR));

  const saveTemplate = useMutation({
    mutationFn: ({ id, patch }: TemplateUpdate) => adminApi.templates.update(id, patch),
    onSuccess: (_result, { id, patch }) => {
      qc.setQueryData<NotificationTemplate[]>(['admin', 'templates'], (templates) =>
        templates?.map((template) => (template.id === id ? { ...template, ...patch } : template)),
      );
      toast.success('Шаблон сохранён');
      qc.invalidateQueries({ queryKey: ['admin', 'templates'] });
      setEditing(null);
    },
    onError: () => toast.error('Не удалось сохранить шаблон'),
  });

  function openEdit(template: NotificationTemplate) {
    setEditing(template);
    setTitleInput(template.title ?? '');
    setBodyInput(template.body);
    setEnabledInput(template.isEnabled);
    setEmbedInput({ color: DEFAULT_EMBED_COLOR, ...(template.embedConfig ?? {}) });
    setColorInput(colorToHex(template.embedConfig?.color));
  }

  function saveEditingTemplate() {
    if (!editing) return;
    const isEmbed = EMBED_TEMPLATE_KEYS.has(editing.key);
    saveTemplate.mutate({
      id: editing.id,
      patch: {
        title: titleInput.trim() || null,
        body: bodyInput,
        isEnabled: enabledInput,
        ...(isEmbed
          ? {
              embedConfig: {
                color: embedInput.color ?? DEFAULT_EMBED_COLOR,
                bannerImageUrl: nullable(embedInput.bannerImageUrl ?? ''),
                imageUrl: nullable(embedInput.imageUrl ?? ''),
                thumbnailUrl: nullable(embedInput.thumbnailUrl ?? ''),
                footerText: nullable(embedInput.footerText ?? ''),
                footerIconUrl: nullable(embedInput.footerIconUrl ?? ''),
              },
            }
          : {}),
      },
    });
  }

  const editingEmbed = editing ? EMBED_TEMPLATE_KEYS.has(editing.key) : false;
  const bannerUrl = safeImageUrl(embedInput.bannerImageUrl);
  const imageUrl = safeImageUrl(embedInput.imageUrl);
  const thumbnailUrl = safeImageUrl(embedInput.thumbnailUrl);
  const footerIconUrl = safeImageUrl(embedInput.footerIconUrl);
  const embedColor = /^#[0-9a-f]{6}$/i.test(colorInput)
    ? colorInput
    : colorToHex(embedInput.color);
  const embedUrlsValid = [
    embedInput.bannerImageUrl,
    embedInput.imageUrl,
    embedInput.thumbnailUrl,
    embedInput.footerIconUrl,
  ].every(isValidHttpsUrl);
  const embedTextLength = titleInput.length + bodyInput.length + (embedInput.footerText?.length ?? 0);

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <MessageSquare className="h-7 w-7 text-eol-pink" />
        <h1 className="text-3xl font-extrabold text-eol-white">Сообщения бота</h1>
      </div>
      <p className="mb-6 text-sm text-eol-desc">
        Настройте Discord-сообщения. В тексте можно использовать{' '}
        <code className="text-eol-pink">{'{nickname}'}</code>,{' '}
        <code className="text-eol-pink">{'{server}'}</code>,{' '}
        <code className="text-eol-pink">{'{product}'}</code>,{' '}
        <code className="text-eol-pink">{'{expiresAt}'}</code>,{' '}
        <code className="text-eol-pink">{'{duration}'}</code>.
      </p>

      {!data?.length ? (
        <p className="text-eol-desc">Шаблоны ещё не созданы. Перезапустите сервер — сид добавит стандартные.</p>
      ) : (
        <div className="space-y-3">
          {data.map((template) => (
            <Card key={template.id}>
              <CardContent className="pb-4 pt-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-eol-white">{KEY_LABELS[template.key] ?? template.key}</p>
                      <code className="font-mono text-xs text-eol-desc">{template.key}</code>
                      {EMBED_TEMPLATE_KEYS.has(template.key) && <Badge>Embed</Badge>}
                      {!template.isEnabled && <Badge variant="danger">Выключен</Badge>}
                    </div>
                    {template.description && <p className="mb-2 text-xs text-eol-desc">{template.description}</p>}
                    <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded border border-white/5 bg-eol-black/40 p-2 text-xs text-eol-desc">
                      {template.body}
                    </pre>
                  </div>
                  <Button size="icon" variant="secondary" onClick={() => openEdit(template)} title="Редактировать">
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editing != null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? KEY_LABELS[editing.key] ?? editing.key : 'Шаблон'}</DialogTitle>
            {editing?.description && <DialogDescription>{editing.description}</DialogDescription>}
          </DialogHeader>

          <div className={editingEmbed ? 'grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.8fr)]' : ''}>
            <div className="space-y-4">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Заголовок</span>
                <Input
                  value={titleInput}
                  maxLength={255}
                  onChange={(event) => setTitleInput(event.target.value)}
                  placeholder="Заголовок сообщения"
                  className="mt-1"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Текст сообщения</span>
                <textarea
                  rows={editingEmbed ? 11 : 10}
                  maxLength={4096}
                  className="mt-1 w-full rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm text-eol-white outline-none focus:border-eol-pink/50"
                  value={bodyInput}
                  onChange={(event) => setBodyInput(event.target.value)}
                />
                <span className="mt-1 block text-right text-[11px] text-eol-desc">{bodyInput.length}/4096</span>
              </label>

              {editingEmbed && (
                <div className="space-y-3 rounded-xl border border-white/5 bg-white/[0.02] p-4">
                  <p className="text-sm font-bold text-eol-white">Оформление embed</p>
                  <div className="grid gap-3 sm:grid-cols-[120px_1fr]">
                    <label>
                      <span className="text-xs text-eol-desc">Цвет</span>
                      <Input
                        type="color"
                        value={embedColor}
                        onChange={(event) => {
                          setColorInput(event.target.value);
                          setEmbedInput((current) => ({ ...current, color: hexToColor(event.target.value) }));
                        }}
                        className="mt-1 h-10 p-1"
                      />
                    </label>
                    <label>
                      <span className="text-xs text-eol-desc">HEX</span>
                      <Input
                        value={colorInput}
                        maxLength={7}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (/^#[0-9a-f]{0,6}$/i.test(value)) {
                            setColorInput(value);
                            if (/^#[0-9a-f]{6}$/i.test(value)) {
                              setEmbedInput((current) => ({ ...current, color: hexToColor(value) }));
                            }
                          }
                        }}
                        className="mt-1 font-mono"
                      />
                    </label>
                  </div>
                  {([
                    ['bannerImageUrl', 'Верхняя картинка', 'https://…/banner.png'],
                    ['imageUrl', 'Нижняя картинка', 'https://…/image.png'],
                    ['thumbnailUrl', 'Миниатюра справа', 'https://…/avatar.png'],
                    ['footerText', 'Текст внизу', 'Empire Loli'],
                    ['footerIconUrl', 'Иконка внизу', 'https://…/icon.png'],
                  ] as const).map(([key, label, placeholder]) => (
                    <label key={key} className="block">
                      <span className="text-xs text-eol-desc">{label}</span>
                      <Input
                        value={embedInput[key] ?? ''}
                        onChange={(event) => setEmbedInput((current) => ({ ...current, [key]: event.target.value }))}
                        placeholder={placeholder}
                        className="mt-1"
                      />
                    </label>
                  ))}
                  {!embedUrlsValid && (
                    <p className="text-xs text-eol-red">Для изображений нужны корректные HTTPS-ссылки.</p>
                  )}
                  {embedTextLength > 6000 && (
                    <p className="text-xs text-eol-red">
                      Общий объём заголовка, текста и подписи превышает лимит Discord 6000 символов.
                    </p>
                  )}
                </div>
              )}

              <label className="flex items-center gap-2 text-eol-white">
                <input
                  type="checkbox"
                  checked={enabledInput}
                  onChange={(event) => setEnabledInput(event.target.checked)}
                />
                Активен (если выключить — сообщение не отправляется)
              </label>
            </div>

            {editingEmbed && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-eol-desc">Предпросмотр</p>
                <div className="space-y-1 rounded-xl bg-[#313338] p-3 shadow-xl">
                  {bannerUrl && (
                    <div className="overflow-hidden rounded bg-[#2b2d31]" style={{ borderLeft: `4px solid ${embedColor}` }}>
                      <img src={bannerUrl} alt="Верхняя картинка embed" className="max-h-48 w-full object-cover" />
                    </div>
                  )}
                  <div className="overflow-hidden rounded bg-[#2b2d31]" style={{ borderLeft: `4px solid ${embedColor}` }}>
                    <div className="flex gap-3 p-4">
                      <div className="min-w-0 flex-1">
                        {titleInput && <p className="mb-2 break-words text-sm font-bold text-white">{titleInput}</p>}
                        <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-[#dbdee1]">
                          {bodyInput || 'Текст сообщения'}
                        </p>
                      </div>
                      {thumbnailUrl && <img src={thumbnailUrl} alt="Миниатюра embed" className="h-16 w-16 shrink-0 rounded object-cover" />}
                    </div>
                    {imageUrl && <img src={imageUrl} alt="Картинка embed" className="max-h-64 w-full object-cover px-4 pb-3" />}
                    {embedInput.footerText && (
                      <div className="flex items-center gap-2 px-4 pb-3 text-[10px] text-[#b5bac1]">
                        {footerIconUrl && <img src={footerIconUrl} alt="" className="h-5 w-5 rounded-full object-cover" />}
                        <span>{embedInput.footerText}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(null)}>Отмена</Button>
            <Button
              onClick={saveEditingTemplate}
              disabled={
                saveTemplate.isPending ||
                !bodyInput.trim() ||
                (editingEmbed && (!embedUrlsValid || embedTextLength > 6000))
              }
            >
              {saveTemplate.isPending ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
